/**
 * Documents global modal host behavior for ERP Accelerator.
 */

"use client";

import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Describes app modal detail data.
 */
export type AppModalDetail = {
  title?: string;
  message?: string | React.ReactNode;
  type?: 'info' | 'warning' | 'error';
  requireConfirm?: boolean; // if true, show Cancel + Confirm; else just OK
  confirmLabel?: string;
  cancelLabel?: string;
};

// Helper to open a modal programmatically
export function openAppModal(detail: AppModalDetail) {
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('app:modal', { detail }));
  }
}

const GlobalModalHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AppModalDetail | null>(null);

  const handleEvent = useCallback((e: Event) => {
    const d = (e as CustomEvent<AppModalDetail>).detail;
    setDetail({
      title: d?.title || (d?.type === 'warning' ? 'Warning' : d?.type === 'error' ? 'Error' : 'Notice'),
      message: d?.message || '',
      type: d?.type || 'info',
      requireConfirm: !!d?.requireConfirm,
      confirmLabel: d?.confirmLabel || 'Confirm',
      cancelLabel: d?.cancelLabel || 'Cancel',
    });
    setOpen(true);
  }, []);

  useEffect(() => {
    document.addEventListener('app:modal', handleEvent as EventListener);
    return () => {
      document.removeEventListener('app:modal', handleEvent as EventListener);
    };
  }, [handleEvent]);

  const onClose = () => setOpen(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {detail?.title}
          </DialogTitle>
          {typeof detail?.message === 'string' ? (
            <DialogDescription>{detail?.message}</DialogDescription>
          ) : (
            detail?.message
          )}
        </DialogHeader>
        <DialogFooter>
          {detail?.requireConfirm ? (
            <>
              <Button variant="outline" onClick={onClose}>
                {detail?.cancelLabel || 'Cancel'}
              </Button>
              <Button onClick={onClose}>
                {detail?.confirmLabel || 'Confirm'}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>OK</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalModalHost;
